import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Input,
  Selection,
  Select,
  SelectItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Autocomplete,
  AutocompleteItem,
  Image,
  Tooltip,
  Modal,
  ModalContent,
  useDisclosure,
  ScrollShadow,
  Divider,
  CardFooter,
  Chip,
} from "@heroui/react";
import { FiSearch } from "react-icons/fi";
import { useSubmit, Link, useLocation, useSearchParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { colors } from "app/utils/searchColors";
import { toKebabCase } from "app/utils/fn";
import { searchProductCatalogClient } from "app/api/product/search-product-catalog.client";
import { getTypesenseNlModelId } from "app/api/typesense/typesense-browser-config";
import { storageKey } from "app/utils/storageKeys";
import { useAtomValue } from "jotai";
import { userAtom } from "app/atoms/user.atom";
import { UserType } from "app/api/user/user.types";
import { useSafeIsMobile } from "app/utils/deviceDetection";
import { MdOutlineSettings } from "react-icons/md";

const sortFilter = [
  { value: "lowest-price", label: "Price, lowest first" },
  { value: "highest-price", label: "Price, highest first" },
  {
    label: "Alphabetical (A-Z)",
    value: "A-Z",
  },
  {
    label: "Alphabetical (Z-A)",
    value: "Z-A",
  },
  { value: "recently added", label: "Recently Added" },
];

const RECENT_SEARCHES_MAX = 8;

function readRecentQueriesFromStorage(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey.recentSearchQueries);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((x: unknown) => typeof x === "string").slice(0, RECENT_SEARCHES_MAX)
      : [];
  } catch {
    return [];
  }
}

function persistRecentQueries(queries: string[]) {
  try {
    localStorage.setItem(
      storageKey.recentSearchQueries,
      JSON.stringify(queries.slice(0, RECENT_SEARCHES_MAX)),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function pushRecentQuery(query: string): string[] {
  const t = query.trim();
  if (!t) return readRecentQueriesFromStorage();
  const prev = readRecentQueriesFromStorage();
  const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(
    0,
    RECENT_SEARCHES_MAX,
  );
  persistRecentQueries(next);
  return next;
}

export const SearchDropdown = () => {
  const user = useAtomValue(userAtom);
  const searchUserType = user?.userType ?? UserType.Non_VIP;
  const isMobile = useSafeIsMobile();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen, onOpenChange, onClose } = useDisclosure();
  const [isPopOpen, setIsPopOpen] = useState(false);

  const [priceSort, setPriceSort] = React.useState<Selection>(new Set([]));
  const [search, setSearchValue] = useState<string>("");
  const [colours, setSearchColours] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<string>(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState<string>(searchParams.get("maxPrice") || "");
  const [minQty, setMinQty] = useState<string>(searchParams.get("minQty") || "");
  const [priceError, setPriceError] = useState<string>("");
  const submit = useSubmit();
  const [fieldState, setFieldState] = React.useState<any>({
    selectedKey: null,
    inputValue: "",
  });
  const [shouldTriggerSearch, setShouldTriggerSearch] = useState<boolean>(false);
  const [recentQueries, setRecentQueries] = useState<string[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (colours.length > 0) count++;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (minQty) count++;
    if (Array.from(priceSort).length > 0) count++;
    return count;
  }, [colours, minPrice, maxPrice, minQty, priceSort]);

  const hasActiveFilters = colours.length > 0 || !!minPrice || !!maxPrice || !!minQty;

  const typesenseNlModelId = useMemo(() => getTypesenseNlModelId(), []);
  const nlHintActive = useMemo(() => {
    const n = search.trim().split(/\s+/).filter(Boolean).length;
    return Boolean(typesenseNlModelId && n > 4);
  }, [search, typesenseNlModelId]);

  // const buttonRef = React.useRef<HTMLButtonElement>(null);
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  // Refs for advanced search modal input wrappers (iOS fix)
  const quantityInputWrapperRef = React.useRef<HTMLDivElement>(null);
  const minPriceInputWrapperRef = React.useRef<HTMLDivElement>(null);
  const maxPriceInputWrapperRef = React.useRef<HTMLDivElement>(null);

  const {
    data: products,
    refetch,
    isLoading,
    isError: searchQueryError,
  } = useQuery({
    queryKey: [
      "searchProducts",
      search,
      minQty,
      minPrice,
      maxPrice,
      colours,
      priceSort,
      searchUserType,
      typesenseNlModelId,
    ],
    queryFn: ({ signal }) => {

      const sortKey = Array.from(priceSort)[0]?.toString();
      return searchProductCatalogClient({
        search: search?.trim(),
        normalSearch: "true",
        limit: 10,
        userType: searchUserType,
        ...(typesenseNlModelId ? { nlQuery: true as const } : {}),
        ...(minQty ? { minQty } : {}),
        ...(minPrice ? { minPrice } : {}),
        ...(maxPrice ? { maxPrice } : {}),
        ...(colours.length > 0 ? { colours } : {}),
        ...(sortKey ? { sort: sortKey } : {}),
        signal,
      });
    },
    enabled: shouldTriggerSearch && !!search?.trim(),
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    setRecentQueries(readRecentQueriesFromStorage());
  }, []);

  useEffect(() => {
    const hasSearchIntent =
      location.pathname === "/search" ||
      searchParams.has("q") ||
      searchParams.has("sort") ||
      searchParams.has("minPrice") ||
      searchParams.has("maxPrice") ||
      searchParams.has("minQty") ||
      searchParams.getAll("colours").length > 0;

    if (!hasSearchIntent) return;

    const q = searchParams.get("q") ?? "";
    setSearchValue(q);
    setFieldState((prev: { selectedKey: React.Key | null; inputValue: string }) => ({
      ...prev,
      inputValue: q,
      selectedKey: q ? null : prev.selectedKey,
    }));

    setSearchColours(searchParams.getAll("colours"));

    const sort = searchParams.get("sort");
    if (sort) {
      setPriceSort(new Set([sort]));
    } else if (!searchParams.has("sort")) {
      setPriceSort(new Set());
    }

    setMinPrice(searchParams.get("minPrice") || "");
    setMaxPrice(searchParams.get("maxPrice") || "");
    setMinQty(searchParams.get("minQty") || "");
  }, [location.pathname, searchParams]);

  // Helper function to blur the input field (needed for iOS keyboard dismissal)
  const blurSearchInput = useCallback(() => {
    // Function to perform the blur
    const performBlur = () => {
      // Try to find and blur the input element within the Autocomplete
      if (autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector("input");
        if (input) {
          input.blur();
          return true;
        }
      }
      // Fallback: blur the currently active element if it's an input
      if (
        document.activeElement &&
        (document.activeElement.tagName === "INPUT" ||
          document.activeElement.tagName === "TEXTAREA")
      ) {
        (document.activeElement as HTMLElement).blur();
        return true;
      }
      return false;
    };

    // Try to blur immediately
    performBlur();

    // Also try after a small delay (iOS sometimes needs this)
    setTimeout(() => {
      performBlur();
    }, 100);
  }, []);

  // Helper function to handle iOS input focus (fixes double-tap issue)
  const handleIOSInputFocus = useCallback(
    (wrapperRef: React.RefObject<HTMLDivElement>) => {
      return (e: React.TouchEvent) => {
        // Only apply fix on mobile devices
        if (isMobile && wrapperRef.current) {
          // Find the input element within the wrapper
          const input = wrapperRef.current.querySelector("input");
          if (input) {
            // Prevent default to avoid double-tap
            e.preventDefault();
            // Focus the input immediately
            setTimeout(() => {
              input.focus();
              // Ensure cursor is visible by setting selection
              const length = input.value.length;
              input.setSelectionRange(length, length);
            }, 0);
          }
        }
      };
  },
    [isMobile],
  );

  const onSearchChange = useCallback(
    (value?: string) => {
      setSearchValue(value || "");
      // Reset search trigger when search text changes
      if (value !== search) {
        setShouldTriggerSearch(false);
      }
    },
    [search],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        // Update URL parameters with current filter values
        const newParams = new URLSearchParams(searchParams);
        if (search) newParams.set("q", search?.trim());
        const price = Array.from(priceSort)[0]?.toString();
        if (price) newParams.set("sort", price);
        if (minQty) newParams.set("minQty", minQty);
        if (minPrice) newParams.set("minPrice", minPrice);
        if (maxPrice) newParams.set("maxPrice", maxPrice);
        if (colours.length > 0) {
          newParams.delete("colours");
          colours.forEach((colour) => newParams.append("colours", colour));
        }
        setSearchParams(newParams);

        const formData = new FormData();
        if (search) {
          formData.set("q", search?.trim());
        }
        if (price) formData.set("sort", price);
        if (minQty) formData.set("minQty", minQty);
        if (minPrice) formData.set("minPrice", minPrice);
        if (maxPrice) formData.set("maxPrice", maxPrice);
        if (colours.length > 0) {
          colours.forEach((colour) => formData.append("colours", colour));
        }

        // Reset search trigger state before submitting
        setShouldTriggerSearch(false);

        // Close any open popovers/modals
        setIsPopOpen(false);
        onClose();

        // Update field state
        setFieldState(() => {
          return {
            inputValue: search,
            selectedKey: search,
          };
        });

        // Blur the input to dismiss keyboard on iOS/iPadOS
        blurSearchInput();

        if (search?.trim()) {
          setRecentQueries(pushRecentQuery(search.trim()));
        }

        // Submit the search
        submit(formData, { method: "get", action: "/search" });
      }
    },
    [
      search,
      submit,
      searchParams,
      setSearchParams,
      priceSort,
      minQty,
      minPrice,
      maxPrice,
      colours,
      onClose,
      blurSearchInput,
    ],
  );

  const onSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (products?.products) {
        setFieldState(() => {
          let selectedItem = products?.products.find((option) => option.slug === key);

          return {
            inputValue: selectedItem?.overview.name || "",
            selectedKey: key,
          };
        });
        // Don't trigger search when selecting from dropdown
        setShouldTriggerSearch(false);

        // Blur the input to dismiss keyboard on iOS/iPadOS when selecting a product
        blurSearchInput();
      }
    },
    [products?.products, blurSearchInput],
  );

  const handleSubmit = useCallback(
    (e: any) => {
      if ("preventDefault" in e) e.preventDefault();
      if (priceError) return;

      // Update URL parameters with current filter values
      const newParams = new URLSearchParams(searchParams);
      if (search) newParams.set("q", search?.trim());
      const price = Array.from(priceSort)[0]?.toString();
      if (price) newParams.set("sort", price);
      if (minQty) newParams.set("minQty", minQty);
      if (minPrice) newParams.set("minPrice", minPrice);
      if (maxPrice) newParams.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        newParams.delete("colours");
        colours.forEach((colour) => newParams.append("colours", colour));
      }
      setSearchParams(newParams);

      const formData = new FormData();
      if (search) formData.set("q", search?.trim());
      if (price) formData.set("sort", price);
      if (minQty) formData.set("minQty", minQty);
      if (minPrice) formData.set("minPrice", minPrice);
      if (maxPrice) formData.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        colours.forEach((colour) => formData.append("colours", colour));
      }
      setIsPopOpen(false);
      onClose();

      // Reset the search trigger state
      setShouldTriggerSearch(false);

      // a hack used to close the popup
      // buttonRef.current?.click();
      // buttonRef.current?.focus();
      // buttonRef.current?.blur();
      setFieldState(() => {
        return {
          inputValue: search,
          selectedKey: search,
        };
      });

      // Blur the input to dismiss keyboard on iOS/iPadOS
      blurSearchInput();

      if (search?.trim()) {
        setRecentQueries(pushRecentQuery(search.trim()));
      }

      submit(formData, { method: "get", action: "/search" });
    },
    [
      search,
      minPrice,
      maxPrice,
      minQty,
      priceSort,
      colours,
      onClose,
      submit,
      searchParams,
      setSearchParams,
      blurSearchInput,
    ],
  );

  const handleSelectColour = useCallback(
    (col: string) => {
      if (colours.includes(col)) {
        setSearchColours((prevCols) => prevCols.filter((cc) => cc !== col));
      } else if (colours.length < 6) {
        setSearchColours((prevCols) => [...prevCols, col]);
      }
      // Don't update URL params until search is clicked
    },
    [colours],
  );

  const handleResetFilter = useCallback(() => {
    setSearchColours([]);
    setMinQty("");
    setMaxPrice("");
    setMinPrice("");
    setPriceSort(new Set());
    setShouldTriggerSearch(false);
    setPriceError(""); // Clear price error when resetting
    // Clear URL params when resetting
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("colours");
    newParams.delete("minQty");
    newParams.delete("minPrice");
    newParams.delete("maxPrice");
    newParams.delete("sort");
    setSearchParams(newParams);
    onClose();
    setIsPopOpen(false);
  }, [onClose, searchParams, setSearchParams]);

  const handleMinQty = useCallback((n: string) => {
    setMinQty(n);
  }, []);

  const handleMinPriceChange = (value: string) => {
    setMinPrice(value);

    // Clear error if either value is empty
    if (!value || !maxPrice) {
      setPriceError("");
      return;
    }

    // Check validation only when both values exist
    if (parseFloat(value) >= parseFloat(maxPrice)) {
      setPriceError("Minimum price must be less than maximum price");
    } else {
      setPriceError("");
    }
  };

  const handleMaxPriceChange = (value: string) => {
    setMaxPrice(value);

    // Clear error if either value is empty
    if (!value || !minPrice) {
      setPriceError("");
      return;
    }

    // Check validation only when both values exist
    if (parseFloat(minPrice) >= parseFloat(value)) {
      setPriceError("Maximum price must be greater than minimum price");
    } else {
      setPriceError("");
    }
  };

  const handleSearchButtonClick = useCallback(() => {
    if (search?.trim()) {
      // Update URL parameters with current filter values
      const newParams = new URLSearchParams(searchParams);
      if (search) newParams.set("q", search?.trim());
      const price = Array.from(priceSort)[0]?.toString();
      if (price) newParams.set("sort", price);
      if (minQty) newParams.set("minQty", minQty);
      if (minPrice) newParams.set("minPrice", minPrice);
      if (maxPrice) newParams.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        newParams.delete("colours");
        colours.forEach((colour) => newParams.append("colours", colour));
      }
      setSearchParams(newParams);

      setShouldTriggerSearch(true);
      refetch();
    }
  }, [
    search,
    refetch,
    searchParams,
    setSearchParams,
    priceSort,
    minQty,
    minPrice,
    maxPrice,
    colours,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (search?.trim()) {
        setShouldTriggerSearch(true);
      } else {
        setShouldTriggerSearch(false);
      }
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  const removeRecentQuery = useCallback((q: string) => {
    setRecentQueries((prev) => {
      const next = prev.filter((x) => x !== q);
      persistRecentQueries(next);
      return next;
    });
  }, []);

  const applyRecentQuery = useCallback((q: string) => {
    setSearchValue(q);
    setFieldState({ inputValue: q, selectedKey: null });
    setShouldTriggerSearch(!!q.trim());
  }, []);

  const sortKeyToLabel = useCallback((key: string) => {
    return sortFilter.find((s) => s.value === key)?.label ?? key;
  }, []);

  const removeSortChip = useCallback(() => {
    setPriceSort(new Set());
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("sort");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearMinPriceChip = useCallback(() => {
    setMinPrice("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("minPrice");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearMaxPriceChip = useCallback(() => {
    setMaxPrice("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("maxPrice");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearMinQtyChip = useCallback(() => {
    setMinQty("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("minQty");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const removeColourChip = useCallback(
    (col: string) => {
      const next = colours.filter((c) => c !== col);
      setSearchColours(next);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("colours");
      next.forEach((c) => newParams.append("colours", c));
      setSearchParams(newParams);
    },
    [colours, searchParams, setSearchParams],
  );

  const dropdownTotal = products?.pagination?.total ?? 0;
  const dropdownShown = products?.products?.length ?? 0;
  const showViewAllResults =
    !searchQueryError && dropdownTotal > 0 && dropdownTotal > dropdownShown;

  const liveStatusMessage = useMemo(() => {
    if (!search?.trim() || !shouldTriggerSearch) return "";
    if (searchQueryError) return "Search failed. Please try again.";
    if (isLoading) return "Searching catalogue.";
    return `${dropdownTotal} product${dropdownTotal === 1 ? "" : "s"} found.`;
  }, [search, shouldTriggerSearch, searchQueryError, isLoading, dropdownTotal]);

  const sortChipKey = Array.from(priceSort)[0]?.toString();

  // Auto-clear price error when values become valid
  useEffect(() => {
    if (minPrice && maxPrice && parseFloat(minPrice) < parseFloat(maxPrice)) {
      setPriceError("");
    }
  }, [minPrice, maxPrice]);

  const popContent = useMemo(
    () => (
      <Popover
        isOpen={isPopOpen}
        onOpenChange={(open) => setIsPopOpen(open)}
        placement="bottom-start"
        backdrop="blur"
        isDismissable={true}
        isKeyboardDismissDisabled={true}
      >
        <PopoverTrigger>
          <Button
            isIconOnly
            variant="light"
            aria-label={`Advanced search settings${
              activeFilterCount > 0
                ? ` (${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""})`
                : ""
            }`}
          >
            <div className="relative">
              <MdOutlineSettings className="w-6 h-6" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none pointer-events-none">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-1 rounded-lg border-2 dark:border-gray-400">
          <Card shadow="none" radius="none" className="border-none bg-transparent">
            <CardBody className="p-3">
              <div className="flex flex-col md:flex-row space-x-4 max-w-[45rem]">
                <div className="space-y-4 p-4">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        inputMode="numeric"
                        label="Quantity"
                        labelPlacement="outside"
                        placeholder="100"
                        variant="bordered"
                        value={minQty}
                        min="0"
                        onValueChange={handleMinQty}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Minimum quantity"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        label="Price from"
                        labelPlacement="outside"
                        placeholder="10.00"
                        startContent="$"
                        variant="bordered"
                        value={minPrice}
                        min="0"
                        onValueChange={handleMinPriceChange}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Minimum price"
                      />
                      <span className="text-xs text-gray dark:text-gray-100 italic">
                        Plus Branding and Freight cost
                      </span>
                    </div>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        label="Price To"
                        labelPlacement="outside"
                        placeholder="12.50"
                        startContent="$"
                        variant="bordered"
                        value={maxPrice}
                        min="0"
                        onValueChange={handleMaxPriceChange}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Maximum price"
                      />
                    </div>
                  </div>
                  {priceError && <div className="text-red-500 text-sm mt-1">{priceError}</div>}
                  <div className="flex flex-col gap-3">
                    <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      Quick Presets
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="flat"
                        color="success"
                        className="h-8 px-3 text-xs font-medium"
                        onPress={() => handleMaxPriceChange("5")}
                      >
                        💰 Under $5
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        className="h-8 px-3 text-xs font-medium"
                        onPress={() => {
                          const ecoQuery = "eco friendly";
                          if (!search.toLowerCase().includes("eco")) {
                            const next = search.trim() ? `${search.trim()} ${ecoQuery}` : ecoQuery;
                            setSearchValue(next);
                            setFieldState({ inputValue: next, selectedKey: null });
                          }
                        }}
                      >
                        🌿 Eco-Friendly
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        color="secondary"
                        className="h-8 px-3 text-xs font-medium"
                        onPress={() => handleMinQty("50")}
                      >
                        📦 Low MOQ (50+)
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Select
                      label="Sort results by:"
                      labelPlacement="outside"
                      placeholder="Price, lowest first"
                      selectedKeys={priceSort}
                      className="w-full"
                      onSelectionChange={setPriceSort}
                      variant="bordered"
                      classNames={{
                        base: "border-zinc-red",
                        value: "border-zinc-red",
                        mainWrapper: "border-zinc-red",
                        listboxWrapper: "border-zinc-red",
                        label: "font-medium",
                      }}
                      aria-label="Sort results"
                    >
                      {sortFilter.map((ss) => (
                        <SelectItem key={ss.value}>{ss.label}</SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 justify-end">
                    <Button
                      onPress={handleResetFilter}
                      variant="flat"
                      color="primary"
                      className="px-4"
                      aria-label="Reset filters"
                    >
                      Reset
                    </Button>
                    <Button
                      onPress={handleSubmit}
                      className="bg-primary text-white px-4"
                      aria-label="Search"
                      isDisabled={!!priceError}
                    >
                      Search
                    </Button>
                  </div>
                </div>
                <div className="space-y-4 p-4">
                  <h3 className="text-lg font-semibold">Search by Colours</h3>
                  <span className="text-sm text-gray dark:text-foreground font-normal">
                    Select one or more colours to highlight products available
                  </span>
                  <div className="grid grid-cols-5 gap-3 w-full bg-zinc-100 dark:bg-zinc-700 p-4 rounded-lg">
                    {colors.map((c, i) => (
                      <Tooltip
                        content={
                          !colours.includes(c) && colours.length >= 6
                            ? "Maximum 6 colors can be selected"
                            : c
                        }
                        key={`${i}-${c}`}
                      >
                        <Button
                          onPress={() => handleSelectColour(c)}
                          isIconOnly
                          className={
                            colours.includes(c)
                              ? "w-11 h-11 aspect-square rounded-full shadow-xl border-2 border-white bg-overlay-black transform scale-105 transition-transform"
                              : "w-10 h-10 aspect-square rounded-full dark:border dark:border-zinc-500"
                          }
                          style={{
                            backgroundColor: c,
                            outline: colours.includes(c) ? `1px dashed ${c}` : "none",
                            opacity: !colours.includes(c) && colours.length >= 6 ? 0.4 : 1,
                          }}
                          aria-label={`Select colour ${c}`}
                        />
                      </Tooltip>
                    ))}
                  </div>
                  <div>
                    {colours.length > 0 && (
                      <span className="pt-2" aria-live="polite">
                        Selected Colours: {colours.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </PopoverContent>
      </Popover>
    ),
    [
      isPopOpen,
      colours,
      priceSort,
      search,
      minQty,
      minPrice,
      maxPrice,
      activeFilterCount,
      handleResetFilter,
      handleMinQty,
      handleSubmit,
      handleSelectColour,
      handleMinPriceChange,
      handleMaxPriceChange,
      priceError,
    ],
  );

  return (
    <div className="w-full">
      <Modal
        placement="top"
        backdrop="blur"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        hideCloseButton={true}
        isDismissable={true}
        isKeyboardDismissDisabled={true}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.3,
                ease: "easeOut",
              },
            },
            exit: {
              y: -20,
              opacity: 0,
              transition: {
                duration: 0.2,
                ease: "easeIn",
              },
            },
          },
        }}
        aria-labelledby="advanced-search-modal"
      >
        <ModalContent className="p-1 rounded-lg border border-foreground-200">
          <Card shadow="none" radius="none" className="border-none bg-transparent p-0">
            <CardBody className="p-0">
              <div className="text-gray dark:text-foreground font-semibold px-3 py-2">
                Advanced Search
              </div>
              <ScrollShadow
                className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl py-3 px-4"
                orientation="horizontal"
                hideScrollBar={false}
              >
                <div className="flex gap-2 w-full">
                  {colors.map((c, i) => (
                    <Tooltip content={c} key={`${i}-${c}`}>
                      <Button
                        onPress={() => handleSelectColour(c)}
                        isIconOnly
                        className={
                          colours.includes(c)
                            ? "w-12 h-12 p-1 aspect-square rounded-full shadow-lg border-4 border-white bg-overlay-black transform scale-110 transition-transform"
                            : "w-10 h-10 aspect-square rounded-full dark:border-2 dark:border-zinc-500"
                        }
                        style={{ backgroundColor: c }}
                        aria-label={`Select colour ${c}`}
                      />
                    </Tooltip>
                  ))}
                </div>
              </ScrollShadow>
              <Divider className="mt-3 mb-2" />
              <div className="p-3">
                <div className="grid gap-3 mb-3">
                  <div
                    ref={quantityInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(quantityInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="Quantity"
                      className="text-primary"
                      variant="bordered"
                      value={minQty}
                      min="0"
                      onValueChange={handleMinQty}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Quantity"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    ref={minPriceInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(minPriceInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="Price from"
                      className="text-primary"
                      startContent="$"
                      variant="bordered"
                      value={minPrice}
                      min="0"
                      onValueChange={handleMinPriceChange}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Price from"
                    />
                    <span className="text-xs text-gray dark:text-gray-100">
                      Plus Branding and Freight cost{" "}
                    </span>
                  </div>
                  <div
                    ref={maxPriceInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(maxPriceInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="Price To"
                      className="text-primary"
                      startContent="$"
                      variant="bordered"
                      value={maxPrice}
                      min="0"
                      onValueChange={handleMaxPriceChange}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Price to"
                    />
                  </div>
                </div>
                {priceError && <div className="text-red-500 text-sm mt-1">{priceError}</div>}
                <div className="flex flex-col gap-2">
                  <Select
                    placeholder="Price, lowest first"
                    selectedKeys={priceSort}
                    className="w-full"
                    onSelectionChange={setPriceSort}
                    classNames={{
                      base: "border-zinc-red",
                      value: "border-zinc-red",
                      mainWrapper: "border-zinc-red",
                      listboxWrapper: "border-zinc-red",
                      label: "font-medium",
                    }}
                    aria-label="Sort results"
                  >
                    {sortFilter.map((ss) => (
                      <SelectItem key={ss.value}>{ss.label}</SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </CardBody>
            <CardFooter className="gap-2 justify-end">
              <Button
                onPress={onClose}
                variant="flat"
                color="primary"
                className="px-4"
                aria-label="Close advanced search"
              >
                Close
              </Button>
              <Button
                onPress={handleSubmit}
                className="bg-primary text-white px-4"
                aria-label="Submit search"
                isDisabled={!!priceError}
              >
                Search
              </Button>
            </CardFooter>
          </Card>
        </ModalContent>
      </Modal>
      <form
        onSubmit={(e) => e.preventDefault()}
        role="search"
        className="w-full relative"
        onKeyDown={handleKeyDown}
      >
        <div className="flex gap-x-1 items-center" ref={autocompleteRef}>
          <Autocomplete
            showScrollIndicators={true}
            // menuTrigger={menuTrigger}
            inputValue={search}
            selectedKey={fieldState.selectedKey}
            isLoading={isLoading}
            scrollShadowProps={{ isEnabled: false }}
            items={
              products && products?.products && products?.products.length > 0
                ? products?.products
                : []
            }
            size="lg"
            variant="bordered"
            placeholder="Search product catalogue"
            radius="sm"
            className="w-full"
            classNames={{
              base: "w-full p-0",
              inputWrapper: [
                "h-full",
                "border-zinc-300",
                "dark:border-zinc-600",
                "group-data-[focus=true]:border-primary",
                "bg-white",
                "dark:bg-zinc-800",
                "rounded-r-none",
                "shadow-sm",
                "!p-0 !m-0",
              ],
              listbox: ["border", "border-zinc-100", "!p-0 !m-0"],
              popoverContent: ["rounded-md", "shadow-xl", "p-0"],
              listboxWrapper: ["rounded-md", "show-box-scrollbar", "p-0"],
            }}
            clearButtonProps={{ onPress: () => onSearchChange("") }}
            popoverProps={{
              isDismissable: true,
              isKeyboardDismissDisabled: true,

              classNames: {
                content: "border border-gray-200 dark:border-gray-500 dark:bg-gray-800",
              },
            }}
            listboxProps={{
              classNames: {
                emptyContent: ["!p-0 !m-0"],
              },
              emptyContent: searchQueryError ? (
                <div className="py-4 px-4 text-center text-sm text-danger" role="alert">
                  Search couldn&apos;t load. Try again in a moment.
                </div>
              ) : isLoading ? (
                <div className="py-4 text-center text-foreground-500">Searching...</div>
              ) : search?.trim() ? (
                <div className="flex flex-col space-y-4">
                  <p className="text-foreground-500 text-center pt-4 px-4">
                    No products found for &quot;{search}&quot;
                  </p>

                  {/* NL Suggestion for short queries */}
                  {search.trim().split(/\s+/).length < 4 && (
                    <div className="bg-primary dark:bg-primary-900/40 p-5 text-center text-white dark:text-primary-100 border-y border-primary-100/20">
                      <p className="font-semibold mb-1 text-sm">
                        💡 Pro Tip: Try a natural sentence
                      </p>
                      <button
                        type="button"
                        onClick={() => applyRecentQuery("blue pens under $2")}
                        className="underline italic text-white/90 hover:text-white transition-colors text-xs"
                      >
                        &quot;Show me blue pens under $2&quot;
                      </button>
                    </div>
                  )}

                  {/* Suggested Categories */}
                  <div className="space-y-3 px-4 pb-4">
                    <p className="text-[10px] font-bold uppercase text-foreground-400 tracking-wider text-center">
                      Try these categories:
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {["T-Shirts", "Pens", "Mugs", "Bags", "Bottles"].map((cat) => (
                        <Button
                          key={cat}
                          size="sm"
                          variant="flat"
                          className="h-8 px-4 text-xs bg-default-100 hover:bg-default-200"
                          onPress={() => applyRecentQuery(cat)}
                        >
                          {cat}
                        </Button>
                      ))}
                    </div>

                    {hasActiveFilters && (
                      <div className="pt-4 border-t border-default-100 text-center">
                        <button
                          type="button"
                          onClick={handleResetFilter}
                          className="text-primary text-xs font-medium underline cursor-pointer hover:text-primary-600 transition-colors"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : recentQueries.length > 0 ? (
                <div className="flex flex-col gap-2 p-3">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      Recent searches
                    </span>
                    <Button
                      size="sm"
                      variant="light"
                      className="h-6 min-w-0 px-2 text-[10px] text-danger"
                      onPress={() => {
                        persistRecentQueries([]);
                        setRecentQueries([]);
                      }}
                    >
                      Clear all
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {recentQueries.map((rq) => (
                      <Chip
                        key={rq}
                        size="sm"
                        variant="flat"
                        onClose={() => removeRecentQuery(rq)}
                        className="cursor-pointer hover:bg-default-200 transition-colors h-7"
                        onClick={() => applyRecentQuery(rq)}
                      >
                        {rq}
                      </Chip>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-4 text-center text-foreground-500">
                  Start typing to search the catalogue
                </div>
              ),
              bottomContent: showViewAllResults ? (
                <div className="m-auto py-2 border-t border-default-200 dark:border-default-100">
                  <Button
                    size="sm"
                    onPress={handleSubmit}
                    variant="flat"
                    color="primary"
                    className="w-full"
                    aria-label={`View all ${dropdownTotal} search results`}
                  >
                    View all {dropdownTotal} results
                  </Button>
                </div>
              ) : null,
            }}
            startContent={
              isMobile ? (
                <Button
                  isIconOnly
                  variant="light"
                  onPress={onOpenChange}
                  aria-label={`Open advanced search${
                    activeFilterCount > 0
                      ? ` (${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""})`
                      : ""
                  }`}
                >
                  <div className="relative">
                    <MdOutlineSettings className="w-6 h-6" />
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none pointer-events-none">
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                </Button>
              ) : (
                popContent
              )
            }
            // onValueChange={onSearchChange}
            onInputChange={onSearchChange}
            onSelectionChange={onSelectionChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setIsInputFocused(true);
              // Refresh recent queries on focus
              setRecentQueries(readRecentQueriesFromStorage());
            }}
            onBlur={() => {
              // Delay blur to allow clicking recent queries
              setTimeout(() => setIsInputFocused(false), 200);
            }}
            aria-label="Search products"
          >
            {(item) => (
              <AutocompleteItem key={item.slug} className="capitalize">
                <Link
                  to={`/category/${
                    item.globalProductCategory.name
                      ? toKebabCase(item.globalProductCategory.name)
                      : "_"
                  }/${
                    item.globalProductSubCategory.name
                      ? toKebabCase(item.globalProductSubCategory.name)
                      : "_"
                  }/${item.slug}`}
                  aria-label={`Go to ${item.overview.name} details`}
                >
                  <div className="flex gap-4 items-center">
                    <Image
                      alt={`Image of ${item.overview.name}`}
                      src={item.overview.heroImage}
                      removeWrapper
                      height={48}
                      width={48}
                      className="!object-scale-down border-2 border-zinc-100"
                      classNames={{
                        wrapper:
                          "!object-scale-down border-2 border-zinc-100 h-12 w-12 transition aspect-square",
                      }}
                      loading="lazy"
                    />
                    <span>{item.overview.name}</span>
                    <span>{item.overview.code}</span>
                  </div>
                </Link>
              </AutocompleteItem>
            )}
          </Autocomplete>
          <Button
            type="button"
            size="md"
            isIconOnly
            radius="md"
            color="primary"
            className=" h-[48px] min-w-[48px]"
            aria-label="Search"
            isDisabled={!!priceError}
            onPress={handleSearchButtonClick}
          >
            <FiSearch size={20} />
          </Button>
        </div>

        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {liveStatusMessage}
        </div>

        {nlHintActive && (
          <p className="text-xs text-foreground-500 mt-1.5 px-0.5">
            Smart search is on for longer queries — results may include filters inferred from your
            sentence.
          </p>
        )}

        {searchQueryError && shouldTriggerSearch && !!search?.trim() && (
          <p className="text-danger text-sm mt-1.5 px-0.5" role="alert">
            Search couldn&apos;t complete. Check your connection and try again.
          </p>
        )}

        {(colours.length > 0 || minPrice || maxPrice || minQty || !!sortChipKey) && (
          <div
            className="mt-2 flex flex-wrap gap-1.5 items-center px-1"
            aria-label="Active search filters"
          >
            <span className="text-[10px] font-bold text-foreground-400 uppercase mr-1">
              Filters:
            </span>
            {colours.map((c) => (
              <Chip
                key={c}
                size="sm"
                variant="flat"
                style={{ backgroundColor: c }}
                classNames={{ base: "border border-default-200 min-w-8 h-6", content: "sr-only" }}
                onClose={() => removeColourChip(c)}
                aria-label={`Remove colour filter ${c}`}
              >
                .
              </Chip>
            ))}
            {minPrice ? (
              <Chip
                size="sm"
                variant="flat"
                color="primary"
                className="h-6"
                onClose={clearMinPriceChip}
              >
                Min ${minPrice}
              </Chip>
            ) : null}
            {maxPrice ? (
              <Chip
                size="sm"
                variant="flat"
                color="primary"
                className="h-6"
                onClose={clearMaxPriceChip}
              >
                Max ${maxPrice}
              </Chip>
            ) : null}
            {minQty ? (
              <Chip
                size="sm"
                variant="flat"
                color="secondary"
                className="h-6"
                onClose={clearMinQtyChip}
              >
                Qty ≥ {minQty}
              </Chip>
            ) : null}
            {sortChipKey ? (
              <Chip
                size="sm"
                variant="flat"
                color="default"
                className="h-6"
                onClose={removeSortChip}
              >
                {sortKeyToLabel(sortChipKey)}
              </Chip>
            ) : null}
          </div>
        )}
      </form>
      {/* <button
        ref={buttonRef}
        hidden={true}
        className="w-0 h-0 border-none outline-none absolute top-0"
        aria-hidden="true"
        tabIndex={-1}
      /> */}
    </div>
  );
};
